import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import { RealFigure, useAvatarAsset } from './realAvatar.jsx';

// آدمکِ بقیه‌ی بازدیدکننده‌ها در بازدیدِ مشترک (سانتی‌متر، داخلِ گروهِ ساختمان). مرد یا زن، با اسم بالای سر.
// جای هر نفر از سرور می‌رسد (۱۰ بار در ثانیه) و این‌جا هر فریم نرم به سمتش می‌رود؛ وقتی راه می‌رود پاها و
// دست‌ها تاب می‌خورند. همه‌چیز با ref و useFrame — بدونِ رندرِ دوباره‌ی React در هر فریم.

const PALETTE = {
  m: { skin: '#e9b78a', hair: '#2f241b', top: '#2f6fb3', bottom: '#2d3139', shoe: '#1c1d21' },
  f: { skin: '#f0c29b', hair: '#5a3522', top: '#c2477a', bottom: '#3a3550', shoe: '#2a1f22' },
};
const MAT = { roughness: 0.65, metalness: 0, envMapIntensity: 0 };
const noRaycast = () => null;

function Mesh({ position, rotation, children, color }) {
  return (
    <mesh position={position} rotation={rotation} raycast={noRaycast}>
      {children}
      <meshStandardMaterial color={color} {...MAT} />
    </mesh>
  );
}

// بدن (رو به −Z محلی، همان قراردادِ دوربینِ اول‌شخص). legRefs/armRefs برای تاب خوردن هنگامِ راه رفتن
function Figure({ char, legRefs, armRefs }) {
  const c = PALETTE[char] || PALETTE.m;
  const female = char === 'f';
  const legTopY = 88;
  const hipTopY = 106;
  const torsoTopY = female ? 146 : 150;
  const neckTopY = torsoTopY + 8;
  const headR = female ? 8.2 : 8.6;
  const headY = neckTopY + headR;
  const shoulder = female ? 19 : 22;
  return (
    <group>
      {/* پاها (از لگن آویزان، هر کدام جدا تاب می‌خورد) */}
      {[-8.5, 8.5].map((x, i) => (
        <group key={`leg${i}`} ref={(el) => (legRefs.current[i] = el)} position={[x, legTopY, 0]}>
          <Mesh position={[0, -(legTopY - 6) / 2, 0]} color={female ? c.skin : c.bottom}>
            <cylinderGeometry args={[female ? 4.6 : 5.4, female ? 5.6 : 6.4, legTopY - 6, 12]} />
          </Mesh>
          <Mesh position={[0, -legTopY + 3, 2]} color={c.shoe}>
            <boxGeometry args={[female ? 9 : 11, 6, female ? 18 : 21]} />
          </Mesh>
        </group>
      ))}
      {female ? (
        // دامن (مخروطِ باز از کمر تا زیرِ زانو)
        <Mesh position={[0, (hipTopY + 52) / 2, 0]} color={c.bottom}>
          <cylinderGeometry args={[14, 23, hipTopY - 52, 18, 1, true]} />
        </Mesh>
      ) : (
        <Mesh position={[0, (legTopY + hipTopY) / 2, 0]} color={c.bottom}>
          <boxGeometry args={[30, hipTopY - legTopY, 18]} />
        </Mesh>
      )}
      {/* تنه */}
      <Mesh position={[0, (hipTopY + torsoTopY) / 2, 0]} color={c.top}>
        <capsuleGeometry args={[female ? 13 : 15, torsoTopY - hipTopY - (female ? 26 : 30), 8, 14]} />
      </Mesh>
      {/* بازوها (از شانه آویزان) */}
      {[-shoulder, shoulder].map((x, i) => (
        <group key={`arm${i}`} ref={(el) => (armRefs.current[i] = el)} position={[x, torsoTopY - 6, 0]}>
          <Mesh position={[0, -(torsoTopY - hipTopY - 14) / 2, 0]} color={c.top}>
            <cylinderGeometry args={[4.2, 4.8, torsoTopY - hipTopY - 14, 10]} />
          </Mesh>
          <Mesh position={[0, -(torsoTopY - hipTopY - 10), 0]} color={c.skin}>
            <sphereGeometry args={[4.6, 10, 10]} />
          </Mesh>
        </group>
      ))}
      {/* گردن و سر */}
      <Mesh position={[0, (torsoTopY + neckTopY) / 2, 0]} color={c.skin}>
        <cylinderGeometry args={[4.6, 5.2, neckTopY - torsoTopY, 10]} />
      </Mesh>
      <Mesh position={[0, headY, 0]} color={c.skin}>
        <sphereGeometry args={[headR, 18, 18]} />
      </Mesh>
      <Mesh position={[0, headY, 0]} color={c.hair}>
        <sphereGeometry args={[headR * 1.07, 18, 12, 0, Math.PI * 2, 0, Math.PI * 0.55]} />
      </Mesh>
      {female && (
        // موی بلندِ پشتِ سر (پشت = +Z محلی)
        <Mesh position={[0, headY - 12, 4.5]} color={c.hair}>
          <capsuleGeometry args={[7.6, 16, 6, 12]} />
        </Mesh>
      )}
      {/* بینی‌ِ کوچک — جهتِ رو را از دور نشان می‌دهد */}
      <Mesh position={[0, headY - 1, -headR + 0.5]} color={c.skin}>
        <sphereGeometry args={[1.8, 8, 8]} />
      </Mesh>
    </group>
  );
}

const lerpAngle = (a, b, t) => {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
};

function RemotePeer({ peer, live, levelAt }) {
  const groupRef = useRef(null);
  const legRefs = useRef([]);
  const armRefs = useRef([]);
  const state = useRef({ x: 0, z: 0, yaw: 0, y: 0, phase: 0, swing: 0, seen: false });
  // آدمکِ واقعی (اگر انتخاب شده و بار شد)؛ وگرنه آدمکِ ساده
  const { asset } = useAvatarAsset(peer.char);
  const motionRef = useRef({ speed: 0, speaking: false });

  useFrame((_, delta) => {
    const g = groupRef.current;
    const p = live.peers.get(peer.id);
    if (!g) return;
    if (!p || !p.pos) {
      g.visible = false;
      return;
    }
    const s = state.current;
    const dt = Math.min(0.1, delta);
    if (!s.seen) {
      s.x = p.pos.x;
      s.z = p.pos.z;
      s.yaw = p.pos.yaw;
      s.seen = true;
    }
    const k = 1 - Math.exp(-dt * 9);
    const px = s.x;
    const pz = s.z;
    s.x += (p.pos.x - s.x) * k;
    s.z += (p.pos.z - s.z) * k;
    s.yaw = lerpAngle(s.yaw, p.pos.yaw, k);
    const targetY = levelAt ? levelAt(s.x, s.z) : 0;
    s.y += (targetY - s.y) * (1 - Math.exp(-dt * 6));
    const speed = Math.hypot(s.x - px, s.z - pz) / Math.max(dt, 1e-3); // سانتی‌متر بر ثانیه
    const walking = speed > 15 || p.pos.m;
    motionRef.current.speed += ((walking ? Math.max(speed, 60) : 0) - motionRef.current.speed) * (1 - Math.exp(-dt * 8));
    motionRef.current.speaking = Boolean(p.speaking && !p.muted);
    s.swing += ((walking ? 1 : 0) - s.swing) * (1 - Math.exp(-dt * 8));
    s.phase += dt * Math.min(9, 3 + speed / 30);
    const a = Math.sin(s.phase) * 0.5 * s.swing;
    if (legRefs.current[0]) legRefs.current[0].rotation.x = a;
    if (legRefs.current[1]) legRefs.current[1].rotation.x = -a;
    if (armRefs.current[0]) armRefs.current[0].rotation.x = -a * 0.8;
    if (armRefs.current[1]) armRefs.current[1].rotation.x = a * 0.8;
    g.visible = true;
    g.position.set(s.x, s.y + (asset ? 0 : Math.abs(Math.sin(s.phase)) * 1.5 * s.swing), s.z);
    g.rotation.y = s.yaw;
  });

  return (
    <group ref={groupRef} visible={false}>
      {asset ? (
        <RealFigure asset={asset} motionRef={motionRef} />
      ) : (
        <Figure char={peer.char === 'f' ? 'f' : 'm'} legRefs={legRefs} armRefs={armRefs} />
      )}
      <Html position={[0, asset ? asset.heightCm + 24 : 196, 0]} center zIndexRange={[20, 0]} style={{ pointerEvents: 'none' }}>
        <div className={`live-tag ${peer.speaking ? 'is-speaking' : ''}`}>
          {peer.muted && <span className="live-tag__muted">🔇</span>}
          {peer.name}
        </div>
      </Html>
    </group>
  );
}

// همه‌ی بقیه — فهرست با ورود/خروجِ آدم‌ها عوض می‌شود (نه با هر حرکت)
export function LivePeers({ live, levelAt }) {
  const [snap, setSnap] = useState(() => live.snapshot());
  useEffect(() => live.subscribe(setSnap), [live]);
  const peers = useMemo(() => snap.peers, [snap]);
  return (
    <group userData={{ khNoExport: true, khLiveKeep: true }}>
      {peers.map((p) => (
        <RemotePeer key={p.id} peer={p} live={live} levelAt={levelAt} />
      ))}
    </group>
  );
}

// جای خودمان را به بقیه می‌فرستد و صدای بقیه را نسبت به جای ما تنظیم می‌کند
export function LiveReporter({ live, posRef, yawRef }) {
  const last = useRef({ x: null, z: null, t: 0 });
  useFrame(() => {
    const p = posRef.current;
    if (!p) return;
    const yaw = yawRef.current || 0;
    const l = last.current;
    const now = performance.now();
    const moving = l.x != null && (Math.abs(p.x - l.x) > 0.3 || Math.abs(p.z - l.z) > 0.3);
    if (moving || now - l.t > 300) {
      l.x = p.x;
      l.z = p.z;
      l.t = now;
    }
    live.sendPos(p.x, p.z, yaw, moving);
    live.updateListener(p.x, p.z, yaw);
  });
  return null;
}
